// ============================================================================
// CrewAhead Maritime Platform - Taxonomy Service
// ============================================================================
// Dynamic, single-source-of-truth access to the Seamaster taxonomy registry.
// Fetches from /api/v1/seamaster/registry as documented in AGENTS.md.
// On any failure the service returns an empty array / null / code fallback,
// never a hardcoded business list.
//
// Prefer the React hooks in components; use the async helpers for imperative
// code (e.g. inside form submit handlers or non-React modules).
// ============================================================================

import { useMemo } from 'react';
import {
  useTaxonomy,
  useTaxonomyCategory,
  useTaxonomyItem,
  useTaxonomyLabel,
  prefetchTaxonomy,
  invalidateTaxonomy,
} from '@/hooks/useTaxonomy';
import { taxonomyAdapter } from './adapters/taxonomy-adapter';
import type {
  TaxonomyCategory,
  TaxonomyItem,
  TaxonomyCache,
} from './adapters/taxonomy-adapter';

export type { TaxonomyCategory, TaxonomyItem, TaxonomyCache };

export {
  useTaxonomy,
  useTaxonomyCategory,
  useTaxonomyItem,
  useTaxonomyLabel,
  prefetchTaxonomy,
  invalidateTaxonomy,
};

// ─── Imperative fetch helpers ────────────────────────────────────────────────

/**
 * Fetch all items for a taxonomy category.
 * Returns an empty array if the registry cannot be fetched or the category
 * is absent — never a hardcoded fallback list.
 */
export async function getTaxonomyItems(
  category: TaxonomyCategory
): Promise<TaxonomyItem[]> {
  try {
    return await taxonomyAdapter.getItems(category);
  } catch {
    return [];
  }
}

/**
 * Fetch a single taxonomy item by its code.
 * Returns null on failure or if the item is missing.
 */
export async function getTaxonomyItem(
  category: TaxonomyCategory,
  code: string
): Promise<TaxonomyItem | null> {
  const items = await getTaxonomyItems(category);
  return items.find((item) => item.code === code) ?? null;
}

/**
 * Fetch the human-readable label for a taxonomy code.
 * Returns the caller-provided fallback (default: the code itself) on failure.
 */
export async function getTaxonomyLabel(
  category: TaxonomyCategory,
  code: string,
  fallback: string = code
): Promise<string> {
  const item = await getTaxonomyItem(category, code);
  return item?.label ?? fallback;
}

// ─── Convenience React helpers ───────────────────────────────────────────────

/**
 * Hook to access multiple taxonomy categories at once.
 * Returns an empty array for any category that is absent or still loading.
 */
export function useTaxonomyCategories<Categories extends TaxonomyCategory[]>(
  ...categories: Categories
) {
  const { data: taxonomy, isLoading, error } = useTaxonomy();

  const result = useMemo(() => {
    const map = {} as {
      [K in Categories[number]]: TaxonomyItem[];
    };
    for (const category of categories) {
      const items = taxonomy?.[category];
      (map as Record<string, TaxonomyItem[]>)[category] = Array.isArray(items)
        ? items
        : [];
    }
    return map;
  }, [taxonomy, categories]);

  return { ...result, isLoading, error };
}

/**
 * Build select options from taxonomy items.
 * Useful for react-hook-form, custom selects, and badge pickers.
 */
export function buildTaxonomyOptions<
  ValueKey extends 'code' | 'id' = 'code'
>(
  items: TaxonomyItem[],
  options: { valueKey?: ValueKey; sortByLabel?: boolean } = {}
): Array<{
  label: string;
  value: ValueKey extends 'id' ? number : string;
  id: number;
}> {
  const { valueKey = 'code' as ValueKey, sortByLabel = false } = options;
  const uniqueItems = items.filter(
    (item, index, self) => self.findIndex((i) => i.id === item.id) === index
  );

  let opts = uniqueItems.map((item) => ({
    id: item.id,
    label: item.label,
    value: (valueKey === 'id' ? item.id : item.code) as ValueKey extends 'id'
      ? number
      : string,
  }));

  if (sortByLabel) {
    opts = opts.sort((a, b) => a.label.localeCompare(b.label));
  }

  return opts;
}

/**
 * Build a code → label lookup map from taxonomy items.
 */
export function buildTaxonomyLabelMap(
  items: TaxonomyItem[]
): Record<string, string> {
  return Object.fromEntries(
    items.map((item) => [item.code, item.label])
  );
}

/**
 * Build an id → label lookup map from taxonomy items.
 * Useful for backend M2M ID arrays.
 */
export function buildTaxonomyIdLabelMap(
  items: TaxonomyItem[]
): Record<number, string> {
  return Object.fromEntries(
    items.map((item) => [item.id, item.label])
  );
}
